/**
 * Geographic math — pure, no DOM, no Leaflet.
 * Distances in nautical miles (nm), bearings in degrees [0, 360).
 */

export const EARTH_RADIUS_M = 6_371_000;
export const METERS_PER_NM = 1852;

export interface LatLon {
  lat: number;
  lon: number;
}

const toRad = (deg: number): number => (deg * Math.PI) / 180;
const toDeg = (rad: number): number => (rad * 180) / Math.PI;

/** Normalize any bearing to [0, 360). */
export function normalizeBearing(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * Great-circle distance in metres (haversine). Used for short deltas between
 * successive GPS fixes where accuracy matters more than the loxodrome.
 */
export function haversineMeters(a: LatLon, b: LatLon): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Great-circle distance in nautical miles. */
export function haversineNm(a: LatLon, b: LatLon): number {
  return haversineMeters(a, b) / METERS_PER_NM;
}

/**
 * Initial great-circle bearing from a to b, degrees [0, 360).
 * Used to derive COG from two fixes.
 */
export function initialBearingDeg(a: LatLon, b: LatLon): number {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLon = toRad(b.lon - a.lon);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return normalizeBearing(toDeg(Math.atan2(y, x)));
}

/**
 * Rhumb-line (loxodrome) distance in nautical miles — constant-bearing sailing,
 * the correct model for the manual route planner (a leg is steered at one heading).
 */
export function rhumbDistanceNm(a: LatLon, b: LatLon): number {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLat = lat2 - lat1;
  let dLon = toRad(b.lon - a.lon);

  const dPhi = Math.log(Math.tan(Math.PI / 4 + lat2 / 2) / Math.tan(Math.PI / 4 + lat1 / 2));
  const q = Math.abs(dPhi) > 1e-12 ? dLat / dPhi : Math.cos(lat1);

  // If crossing the antimeridian, take the shorter route.
  if (Math.abs(dLon) > Math.PI) {
    dLon = dLon > 0 ? -(2 * Math.PI - dLon) : 2 * Math.PI + dLon;
  }

  const dist = Math.sqrt(dLat * dLat + q * q * dLon * dLon) * EARTH_RADIUS_M;
  return dist / METERS_PER_NM;
}

const EARTH_RADIUS_NM = EARTH_RADIUS_M / METERS_PER_NM;

/**
 * Signed cross-track distance (nm) of `point` from the great-circle leg
 * start→end. Positive = starboard (right of the intended track), negative =
 * port. Good approximation for the short legs a follow-route uses (spec 05).
 */
export function crossTrackErrorNm(point: LatLon, start: LatLon, end: LatLon): number {
  const d13 = haversineNm(start, point) / EARTH_RADIUS_NM; // angular
  const brg13 = toRad(initialBearingDeg(start, point));
  const brg12 = toRad(initialBearingDeg(start, end));
  const dxt = Math.asin(Math.sin(d13) * Math.sin(brg13 - brg12));
  return dxt * EARTH_RADIUS_NM;
}

/**
 * Distance (nm) from `start` projected along the leg toward `end` — how far
 * down the track the point is. Used for waypoint auto-advance (abeam) detection.
 */
export function alongTrackDistanceNm(point: LatLon, start: LatLon, end: LatLon): number {
  const d13 = haversineNm(start, point) / EARTH_RADIUS_NM;
  const brg13 = toRad(initialBearingDeg(start, point));
  const brg12 = toRad(initialBearingDeg(start, end));
  const dxt = Math.asin(Math.sin(d13) * Math.sin(brg13 - brg12));
  const dat = Math.acos(Math.cos(d13) / Math.cos(dxt));
  // Sign: negative when the point is behind the start along the track.
  const sign = Math.cos(brg12 - brg13) < 0 ? -1 : 1;
  return sign * dat * EARTH_RADIUS_NM;
}

/**
 * Great-circle destination from `from`, travelling `distanceNm` along `bearingDeg`.
 * Used for the course-predictor vector (where own-ship reaches in N minutes).
 */
export function destinationPoint(from: LatLon, bearingDeg: number, distanceNm: number): LatLon {
  const ang = distanceNm / EARTH_RADIUS_NM; // angular distance (radians)
  const brg = toRad(bearingDeg);
  const lat1 = toRad(from.lat);
  const lon1 = toRad(from.lon);
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(ang) + Math.cos(lat1) * Math.sin(ang) * Math.cos(brg),
  );
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(brg) * Math.sin(ang) * Math.cos(lat1),
      Math.cos(ang) - Math.sin(lat1) * Math.sin(lat2),
    );
  return { lat: toDeg(lat2), lon: ((toDeg(lon2) + 540) % 360) - 180 };
}

/** Constant rhumb-line bearing from a to b, degrees [0, 360). */
export function rhumbBearingDeg(a: LatLon, b: LatLon): number {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  let dLon = toRad(b.lon - a.lon);

  const dPhi = Math.log(Math.tan(Math.PI / 4 + lat2 / 2) / Math.tan(Math.PI / 4 + lat1 / 2));
  if (Math.abs(dLon) > Math.PI) {
    dLon = dLon > 0 ? -(2 * Math.PI - dLon) : 2 * Math.PI + dLon;
  }
  return normalizeBearing(toDeg(Math.atan2(dLon, dPhi)));
}
